import { Add, Check, Search, TrendingUp } from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { polygonApi, StockSearchResult } from '../services/polygon.js';

// Custom debounce function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: number;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay) as any;
  };
}

interface StockSearchBarProps {
  onStockSelect?: (stock: StockSearchResult) => void;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  clearOnSelect?: boolean;
  onAddToPortfolio?: (stock: StockSearchResult) => Promise<void> | void;
  isInPortfolio?: (symbol: string) => boolean;
}

export const StockSearchBar: React.FC<StockSearchBarProps> = ({
  onStockSelect,
  placeholder = 'Search stocks (e.g., AAPL, Apple, Microsoft)...',
  size = 'small',
  fullWidth = false,
  clearOnSelect = false,
  onAddToPortfolio,
  isInPortfolio,
}) => {
  const [searchOptions, setSearchOptions] = useState<StockSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<StockSearchResult | null>(null);
  const [addingSymbols, setAddingSymbols] = useState<Set<string>>(new Set());

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) {
        setSearchOptions([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const normalizedQuery = query.trim().toUpperCase();
        const results = await polygonApi.searchStocks(query, 10);
        const prioritized = results.slice().sort((a, b) => {
          const score = (option: StockSearchResult) => {
            const symbol = option.symbol?.toUpperCase() ?? '';
            if (normalizedQuery.length === 0) return 0;
            if (symbol === normalizedQuery) return 3;
            if (symbol.startsWith(normalizedQuery)) return 2;
            if (symbol.includes(normalizedQuery)) return 1;
            return 0;
          };
          return score(b) - score(a);
        });
        setSearchOptions(prioritized);
      } catch (error) {
        console.error('Stock search error:', error);
        setSearchOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  // Effect for search when input changes
  useEffect(() => {
    if (inputValue && inputValue.length >= 2) {
      debouncedSearch(inputValue);
    } else {
      setSearchOptions([]);
      setLoading(false);
    }
  }, [inputValue, debouncedSearch]);

  const handleStockSelect = (_event: any, value: StockSearchResult | null) => {
    if (value && onStockSelect) {
      onStockSelect(value);
    }
    if (clearOnSelect) {
      setSelectedValue(null);
      setInputValue('');
    } else {
      setSelectedValue(value);
    }
  };

  const getMarketColor = (market: string) => {
    switch (market?.toUpperCase()) {
      case 'STOCKS':
        return 'primary';
      case 'CRYPTO':
        return 'secondary';
      case 'FX':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleAddClick = async (
    event: React.MouseEvent,
    option: StockSearchResult,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (!onAddToPortfolio) return;

    const symbol = option.symbol?.toUpperCase();
    if (!symbol || addingSymbols.has(symbol)) {
      return;
    }

    setAddingSymbols((prev) => {
      const next = new Set(prev);
      next.add(symbol);
      return next;
    });
    try {
      await onAddToPortfolio(option);
    } catch (error) {
      console.error('StockSearchBar add-to-portfolio error', error);
    } finally {
      setAddingSymbols((prev) => {
        const next = new Set(prev);
        next.delete(symbol);
        return next;
      });
    }
  };

  const renderOption = (props: any, option: StockSearchResult) => {
    const symbol = option.symbol?.toUpperCase();
    const inPortfolio = symbol ? isInPortfolio?.(symbol) ?? false : false;
    const adding = symbol ? addingSymbols.has(symbol) : false;

    return (
      <Box component="li" {...props} sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
          <TrendingUp sx={{ color: 'primary.main' }} />
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {option.symbol}
              </Typography>
              <Chip
                label={option.market}
                size="small"
                color={getMarketColor(option.market) as any}
                variant="outlined"
                sx={{ height: '20px', fontSize: '0.7rem' }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {option.name}
            </Typography>
          </Box>
          {onAddToPortfolio && symbol && (
            <Tooltip title={inPortfolio ? 'In portfolio' : 'Add to portfolio'}>
              <span>
                <IconButton
                  size="small"
                  color={inPortfolio ? 'success' : 'primary'}
                  disabled={inPortfolio || adding}
                  onClick={(event) => handleAddClick(event, option)}
                  aria-label={inPortfolio ? 'In portfolio' : 'Add to portfolio'}
                >
                  {adding ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : inPortfolio ? (
                    <Check fontSize="small" />
                  ) : (
                    <Add fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Autocomplete
      size={size}
      open={open}
      value={selectedValue}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={searchOptions}
      loading={loading}
      getOptionLabel={(option) => `${option.symbol} - ${option.name}`}
      isOptionEqualToValue={(option, value) => option.symbol === value.symbol}
      renderOption={renderOption}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={handleStockSelect}
      filterOptions={(x) => x} // Disable built-in filtering since we do server-side search
      sx={{ width: fullWidth ? '100%' : undefined }}
      PaperComponent={({ children, ...other }) => (
        <Paper {...other} elevation={3} sx={{ mt: 1 }}>
          {children}
        </Paper>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          variant="outlined"
          fullWidth={fullWidth}
          sx={{
            minWidth: fullWidth ? undefined : { xs: 250, sm: 350, md: 400 },
            width: fullWidth ? '100%' : undefined,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <IconButton size="small" sx={{ p: 0, mr: 1 }}>
                <Search />
              </IconButton>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={
        inputValue.length < 2 
          ? "Type at least 2 characters to search"
          : loading 
          ? "Searching..."
          : "No stocks found"
      }
    />
  );
}; 
